import React, { useState } from 'react';
import { Alert, App, Modal } from 'antd';

import RecordBadge, { RecordIdentity } from '../common/RecordBadge';
import Guidance from '../common/Guidance';
import { copiedFilesParts, copyReferences, publicationRefusal } from '../../services/publication';
import { describeLinkError } from '../../utils/collectionLinks';
import { apiErrorCode } from '../../utils/apiErrors';

const COPIES_REFUSED = new Set(['copies_not_allowed', 'export_not_allowed']);

/**
 * COPY INTO THIS COLLECTION (docs/plans/personal-discover-global.md §4.6):
 * keep what this collection links to in a global collection by copying it
 * here. The dialog leads with the identity of what is copied, says what will
 * be created, carries the guidance, and has one button. `itemIds` narrows
 * the copy to those records (a single field's value); without it, everything
 * this collection links to in the source is copied.
 *
 * `onLinksStale()` runs when a linked source refuses the copy because it no
 * longer lets this collection keep copies (403 `copies_not_allowed` /
 * `export_not_allowed`): what the caller read about the link is stale.
 *
 * `onDone(report)` gets the API's report — `copies` maps each source record
 * to its copy, so an open form can swap the value it holds.
 */
export default function CopyReferencesModal({ open, onClose, onDone, onLinksStale, collectionId, source, itemIds = null, title = null, variant = 'global' }) {
	const { message } = App.useApp();
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState(null);
	const one = Array.isArray(itemIds) && itemIds.length === 1;
	// 'linked': the source shares with this collection through a collection
	// link that lets it keep copies (keepr-api § Collection links, Copies) —
	// the same endpoint, gated by the link's `allowCopies`.
	const linked = variant === 'linked';
	const sourceName = source?.name || (linked ? 'the linked collection' : 'the global collection');

	const submit = async () => {
		setBusy(true);
		setError(null);
		try {
			const report = await copyReferences(collectionId, { sourceCollectionId: source.collectionId, ...(itemIds ? { itemIds } : {}) });
			const parts = [];
			if (report.itemsCopied) parts.push(report.itemsCopied === 1 ? '1 record copied' : `${report.itemsCopied} records copied`);
			if (report.cardsCloned) parts.push(report.cardsCloned === 1 ? '1 card added' : `${report.cardsCloned} cards added`);
			// Files travel with their records (keepr-api PR 10d): what came, and what could not.
			parts.push(...copiedFilesParts(report));
			message.success(parts.length ? `${parts.join(' · ')}.` : 'Already copied — nothing new to copy.');
			onDone?.(report);
		} catch (e) {
			// The source turned copies off after this collection read its
			// links: the caller reads them again, so the offer goes away.
			if (linked && COPIES_REFUSED.has(apiErrorCode(e)) && typeof onLinksStale === 'function') onLinksStale();
			setError(linked ? describeLinkError(e, 'The records could not be copied.') : publicationRefusal(e, 'The records could not be copied.'));
		} finally {
			setBusy(false);
		}
	};

	return (
		<Modal
			open={open}
			title="Copy into this collection"
			okText="Copy into this collection"
			okButtonProps={{ loading: busy }}
			onOk={submit}
			onCancel={onClose}
			destroyOnClose
		>
			<div className="modal-identity">
				<RecordIdentity
					kicker={one ? 'Record' : 'Records you link to'}
					badge={<RecordBadge kind="collection" record={{ name: sourceName }} title={one && title ? title : sourceName} />}
					below={one ? `From ${sourceName}` : null}
				/>
			</div>
			<p>
				{one
					? 'A copy of this record is made here, with a copy of its card if this collection does not have one yet. This field then points at your copy.'
					: `A copy of every record this collection links to in ${sourceName} is made here, with copies of their cards, and your links are pointed at the copies.`}
				{' '}{one ? 'Its files come with it' : 'Their files come with them'} if this collection allows attachments.
			</p>
			{linked ? (
				<Guidance
					tone="info"
					title="Your copy is yours"
					why={`It stops following ${sourceName}: later changes there do not reach it, and it stays here if the link ends. Other references to ${sourceName}'s records still point at the originals until you copy those too.`}
					links={['sharing-and-export/link-collections']}
				/>
			) : (
				<Guidance
					tone="info"
					title="Your copy is yours"
					why="It stops following the global collection: later changes there do not reach it. Links you make to other records of it still point at the original until you copy those too."
					links={['items/copy-a-record-from-a-retiring-collection', 'items/link-to-a-global-collection']}
				/>
			)}
			{error ? <Alert type="error" showIcon message={error} /> : null}
		</Modal>
	);
}
